import { ArrowUpRight, Briefcase, Mic, Newspaper, Volume2 } from "lucide-react";

import type { GtmBrain } from "../api/GtmApi";
import { voiceDocs, type VoiceDocId } from "./voiceProfile";

type Props = {
  brain: GtmBrain;
  onOpenDoc: (docId: VoiceDocId) => void;
  onBackToOverview: () => void;
};

function DocIcon({ icon }: { icon: "tone" | "linkedin" | "blog" }) {
  const cls = "h-4 w-4 text-teal-600";
  if (icon === "linkedin") return <Briefcase className={cls} />;
  if (icon === "blog") return <Newspaper className={cls} />;
  return <Mic className={cls} />;
}

export default function BrainVoicePanel({ brain, onOpenDoc, onBackToOverview }: Props) {
  const docs = voiceDocs(brain);

  return (
    <div className="flex h-full w-[22rem] shrink-0 flex-col border-r border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-4 py-3 text-xs text-slate-500">
        <button
          type="button"
          className="hover:text-slate-800 hover:underline"
          onClick={onBackToOverview}
        >
          Brain
        </button>
        <span className="mx-1.5 text-slate-300">›</span>
        <span className="font-medium text-slate-800">Voice</span>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-5">
        <div className="mb-1 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-50">
            <Volume2 className="h-4 w-4 text-teal-600" />
          </span>
          <h2 className="text-base font-semibold text-slate-900">Voice</h2>
        </div>
        <p className="mb-5 text-sm text-slate-500">
          How you sound, and how each channel should read.
        </p>

        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {docs.map((doc) => (
            <li key={doc.id}>
              <button
                type="button"
                className="flex w-full items-center gap-3 px-3 py-3.5 text-left transition-colors hover:bg-slate-50"
                onClick={() => onOpenDoc(doc.id)}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-50">
                  <DocIcon icon={doc.icon} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-slate-900">{doc.label}</span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    {doc.filled} of {doc.total} sections filled
                  </span>
                </span>
                <ArrowUpRight className="h-4 w-4 shrink-0 text-slate-400" />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
